/* eslint-disable react/destructuring-assignment */
import React, { useEffect, useMemo, useState } from 'react';
import { bindActionCreators } from 'redux';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import * as reportActions from '../../../../core/report/reportActions';
import * as globalActions from '../../../../core/global/globalActions';
import Layout from '../../../../components/layout/Layout';
import FilterSidebar from '../../../../components/FilterSidebar';
import GridView from '../../../../components/elements/GridView/index';
import ReportFilter, { DATA_MODE_ASSETS_OR_DIVISIONS } from '../../ReportFilter';
import dayjs, { Dayjs } from 'dayjs';
const advancedFormat = require("dayjs/plugin/advancedFormat");
dayjs.extend(advancedFormat);
import { CheckmarkFilled, CloseFilled, HelpFilled, WarningAltFilled } from '@carbon/icons-react';
import { Button, Tooltip } from 'antd';
import { isEmpty, size } from 'underscore';
import CustomEmpty, { CustomEmptyType } from '../../../../components/CustomEmpty';
import ExpandedDvrHealthReportModal from './modal/ExpandedDvrHealthReportModal';
import { RootState } from '../../../../core/reducers';
import { DvrHealthStatus } from '../../../../types/types';
import { getPaginationTotal } from '../../../../components/elements/GridView/GridViewBase/Pagination';

import './dvrHealthReport.scss';

const { REPORT_TYPES } = require('../../../../core/constants').default;

interface Column {
    title: React.ReactNode;
    dataIndex: string;
    key: string;
    fixed?: string;
    width?: number;
    minWidth?: number;
    className?: string;
    sorter?: any;
    render?: (text: any, row: any, index: number) => any;
}

/**
 * Perform the report search. Data will automatically populate to the page as it enter redux via Saga.
 * @param values object containing sidebar values and pagination/sort etc.
 * @param actions actions redux object constructed by bindActionCreators()
 * @param setFilters setter for the filters between renders
 * @returns null
 */
const performDvrHealthReportSearch = (
    values: any,
    actions: any,
    setFilters: React.Dispatch<any>,
) => {
    const filters = values;
    if (!filters?.page) {
        filters.page = 1;
    }
    setFilters(filters);
    actions.getDvrHealthReportRequest({ ...filters });
};

/**
 * Get an array of date objects each one representing a day over the last two weeks.
 * @returns Dayjs[]
 */
const calculateDatesLastTwoWeeks = () => {
    const datesLastTwoWeeks: Dayjs[] = [];
    for (let i = 13; i >= 0; i--) {
        const currentDate = dayjs().subtract(i, 'day');
        const formattedDate = currentDate;
        datesLastTwoWeeks.push(formattedDate);
    }
    return datesLastTwoWeeks;
}

interface DvrHealthReportProps { }

const DvrHealthReport: React.FC<DvrHealthReportProps> = () => {
    const [filters, setFilters] = useState<any>({});
    const [currentPage, setCurrentPage] = React.useState(1);
    const [selectedAssetId, setSelectedAssetId] = useState<number | null>(null);
    const [selectedDate, setSelectedDate] = useState<string | null>(null);
    const [selectedStatus, setSelectedStatus] = useState<DvrHealthStatus | null>(null);

    const user: any = useSelector((state: RootState) => state.user);
    const isFetching: boolean = useSelector((state: RootState) => state.report.isFetching);
    const dvrHealthReport: any = useSelector((state: RootState) => state.report.dvrHealthReport);
    const dvrHealthReportPagination: any = useSelector((state: RootState) => state.report.dvrHealthReportPagination);
    const dispatch = useDispatch();
    const actionOptions: any = {
        ...reportActions,
        ...globalActions,
    };
    const actions: any = useMemo(() => bindActionCreators(
        actionOptions,
        dispatch,
    ), []);

    useEffect(() => {
        actions.resetReportsRequest();
        setCurrentPage(1);
        const params = {
            page: 1,
        };
        performDvrHealthReportSearch(params, actions, setFilters);
    }, []);
    useEffect(() => {
        if (!isFetching) {
            setTimeout(() => {
                const lastColEls = document.getElementsByClassName('scroll-target-last-col');
                if (lastColEls?.length) {
                    const lastColEl = lastColEls[0];
                    const offsetLeft = lastColEl.getBoundingClientRect().left;
                    const tableBodyEls = document.getElementsByClassName('ant-table-body');
                    if (tableBodyEls?.length) {
                        const tableBodyEl = tableBodyEls[0];
                        tableBodyEl.scrollTo({ left: offsetLeft });
                    }
                }
            }, 100);
        }
    }, [isFetching]);

    const onPageChange = (page, sorter) => {
        const order = sorter?.order;
        const sort_by = order ? sorter.field : undefined;
        if (filters?.order !== order || filters?.sort_by !== sort_by) {
            page = 1;
        }

        setCurrentPage(page);
        const newFilter = { ...filters, page, order, sort_by };
        performDvrHealthReportSearch(newFilter, actions, setFilters);
    }
    const TABLE_BTN_SIZE = 24;
    const columns: Column[] = [{
        title: "Asset",
        dataIndex: "asset_name",
        key: "asset_name",
        fixed: 'left',
        sorter: (a, b) => a.asset_name - b.asset_name,
        width: 120,
    }, {
        title: "Division",
        dataIndex: "division_name",
        key: "division_name",
        fixed: 'left',
        sorter: (a, b) => a.division_name - b.division_name,
        width: 120,
    }];
    const datesLastTwoWeeks = calculateDatesLastTwoWeeks();
    datesLastTwoWeeks.forEach((dateObj, i) => {
        const field = dateObj.format('YYYY-MM-DD');
        const column: Column = {
            title: (
                <div className="date-display-col">
                    <div style={{ textAlign: 'center', marginBottom: 0 }}>{dateObj.format('ddd')}</div>
                    <div style={{ textAlign: 'center' }}>{dateObj.format('Do')}</div>
                </div>
            ),
            dataIndex: field,
            key: field,
            width: 60
        };
        if (i === datesLastTwoWeeks.length - 1) {
            column.className = 'scroll-target-last-col';
        }
        column.render = (text, row, index) => {
            const data = dvrHealthReport[index];
            const obj = {
                children: text,
                props: { colSpan: !data.has_dvr || !data.is_supported ? 0 : 1 },
            };

            if (!data.has_dvr && i === 0) {
                obj.props.colSpan = datesLastTwoWeeks.length;
                obj.children = (<div>This asset doesn’t have a DVR installed. Let us know if you'd like to explore options.</div>);
            } else if (!data.is_supported && i === 0) {
                obj.props.colSpan = datesLastTwoWeeks.length;
                obj.children = (<div>Support for this DVR model is coming soon — hang tight!</div>);
            }

            return obj;
        }

        columns.push(column);
    });
    const listReports = dvrHealthReport.map((report) => {
        let output = {
            asset_name: (
                <Link
                    to={`/assets/view/${report.asset_id}`}
                    className="btn-actions"
                >
                    {report.asset_name}
                </Link>
            ),
            division_name: report.division_name || '-',
        };
        datesLastTwoWeeks.forEach(dateObj => {
            const field = dateObj.format('YYYY-MM-DD');
            let icon = (
                <HelpFilled
                    size={TABLE_BTN_SIZE}
                    fill='var(--cool-gray-40)'
                />
            );
            let tooltip = 'No data';
            let isClickable = false;
            let status = DvrHealthStatus.NoData;
            if (report?.[field]?.has_errors) {
                icon = (
                    <CloseFilled
                        size={TABLE_BTN_SIZE}
                        fill='#B81D13'
                        className='dvr-critical-icon'
                    />
                );
                tooltip = 'Critical Issue, click for more info';
                isClickable = true;
                status = DvrHealthStatus.Critical;
            } else if (report?.[field]?.has_alarms) {
                icon = (
                    <WarningAltFilled
                        size={TABLE_BTN_SIZE}
                        fill='#EFB700'
                        className='dvr-warning-icon'
                    />
                );
                tooltip = 'Warning, click for more info';
                isClickable = true;
                status = DvrHealthStatus.Warning;
            } else if (report?.[field]?.has_run) {
                icon = (
                    <CheckmarkFilled
                        size={TABLE_BTN_SIZE}
                        fill='#008450'
                        className='dvr-healthy-icon'
                    />
                );
                tooltip = 'Healthy, click for more info';
                isClickable = true;
                status = DvrHealthStatus.Healthy;
            }
            output[field] = (
                <div style={{
                    width: `${TABLE_BTN_SIZE}px`,
                    height: `${TABLE_BTN_SIZE}px`,
                    overflow: 'hidden'
                }}>
                    <Tooltip title={tooltip}>
                        <Button
                            className='btn btn-link'
                            id={isClickable ? 'dvr-report-modal-btn' : 'dvr-report-modal-btn-disabled'}
                            onClick={() => {
                                if (isClickable && report?.[field]?.has_run) {
                                    setSelectedAssetId(report.asset_id);
                                    setSelectedDate(field);
                                    setSelectedStatus(status);
                                }
                            }}
                            style={{
                                verticalAlign: 'middle',
                                cursor: isClickable ? 'pointer' : 'default',
                                width: `${TABLE_BTN_SIZE}px`,
                                height: `${TABLE_BTN_SIZE}px`,
                            }}
                            disabled={!isClickable}
                        >
                            {icon}
                        </Button>
                    </Tooltip>
                </div>
            );
        });
        return output;
    });
    const customEmptyComponent = (
        <CustomEmpty
            type={CustomEmptyType.Report}
            showIcon
        />
    );

    return (
        <Layout
            className="dvr-health-report"
            title={<div>DVR Health Report</div>}
            headerTitle={<div>DVR Health Report</div>}
            loading={isFetching}
        >
            <div className={`content-wrap${user?.userCompany?.current_alert ? '-with-banner' : ''}`}>
                <FilterSidebar displayFilter>
                    <ReportFilter
                        onSubmit={(filters) => {
                            setCurrentPage(1);
                            actions.resetReportsRequest();
                            const newFilter = { ...filters, page: 1 };
                            performDvrHealthReportSearch(newFilter, actions, setFilters);
                        }}
                        filters={filters}
                        dateMode={null}
                        dataMode={DATA_MODE_ASSETS_OR_DIVISIONS}
                        showDivision
                        showDvrHealthReportsType
                        reportType={REPORT_TYPES.find((rt) => rt.link === 'dvr-health-report-extended').link}
                    />
                </FilterSidebar>
                <div
                    className="content"
                    style={{ opacity: isFetching ? 0 : 1 }}
                >
                    <div className="content-inner">
                        <section className="section">
                            <div className="section__body">
                                <span className='report-title'>DVR Health Report</span>
                                {isEmpty(listReports) || listReports.length < 1 ? customEmptyComponent : (
                                    <>
                                        <GridView
                                            data={listReports}
                                            customColumns={columns}
                                            onChange={(pagination, filters, sorter, extra) => { onPageChange(pagination.current, sorter) }}
                                            scroll={{
                                                x: 'max-content',
                                                y: 'calc(100vh - 240px)',
                                            }}
                                            sortableColumns={['asset_name', 'division_name']}
                                            pagination={{
                                                total: dvrHealthReportPagination.totalRecords,
                                                pageSize: dvrHealthReportPagination.perPageCount,
                                                showSizeChanger: false,
                                                showTotal: getPaginationTotal,
                                                current: currentPage,
                                            }}
                                        />
                                        <ExpandedDvrHealthReportModal
                                            selectedAssetId={selectedAssetId}
                                            selectedDate={selectedDate}
                                            selectedStatus={selectedStatus}
                                            isModalOpen={!!selectedAssetId}
                                            closeModal={() => {
                                                setSelectedAssetId(null);
                                                setSelectedDate(null);
                                            }}
                                        />
                                    </>
                                )}
                            </div>
                        </section>
                    </div>
                </div>
            </div>
        </Layout>
    );
}

export default DvrHealthReport;
